import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Navigation from "@/components/Navigation";
import ContactFooter from "@/components/ContactFooter";
import { Reveal } from "@/components/anim";
import api from "@/lib/api";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return "";
  }
}

export default function NewsListPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/news").then(({ data }) => setItems(data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="bg-bg-primary min-h-screen">
      <Navigation />
      <section className="pt-40 md:pt-52 pb-24 bg-bg-dark">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <Reveal>
            <p className="text-brand-teal text-xs font-bold uppercase tracking-[0.25em] mb-4">Newsroom</p>
            <h1 className="font-heading font-extrabold uppercase text-white text-5xl md:text-7xl lg:text-8xl leading-[0.85] tracking-tighter">
              News &<br />Announcements
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24" data-testid="news-list">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          {loading && <p className="text-ink-soft py-10 border-t border-brand-navy/15">Loading…</p>}
          {!loading && items.length === 0 && (
            <p className="text-ink-soft py-10 border-t border-brand-navy/15">No articles published yet.</p>
          )}
          {items.map((n, i) => (
            <Reveal key={n._id} delay={i * 0.04}>
              <Link
                to={`/news/${n.slug}`}
                data-testid={`news-list-item-${i}`}
                className="group grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 items-start border-t border-brand-navy/15 py-10"
              >
                <div className="md:col-span-3">
                  <span className="text-xs uppercase tracking-[0.15em] text-brand-red font-semibold">{n.category}</span>
                  <p className="text-ink-soft text-sm mt-1">{formatDate(n.published_at)}</p>
                  {n.location && <p className="text-ink-soft text-sm">{n.location}</p>}
                </div>
                <div className="md:col-span-8">
                  <h2 className="font-heading text-2xl md:text-4xl text-ink leading-tight tracking-tight group-hover:text-brand-navy transition-colors">
                    {n.title}
                  </h2>
                  <p className="mt-3 text-ink-soft text-base md:text-lg leading-relaxed line-clamp-3">{n.excerpt}</p>
                </div>
                <div className="md:col-span-1 flex md:justify-end">
                  <ArrowUpRight className="text-brand-navy transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </div>
              </Link>
            </Reveal>
          ))}
          <div className="border-t border-brand-navy/15" />
        </div>
      </section>

      <ContactFooter />
    </div>
  );
}
